import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getChildrenItems } from '../services/getChildrenItems';
import { useQwanyx } from 'qwanyx';
import { useStackDragDrop } from '../hooks/useStackDragDrop';
import {
    transformToTreeData,
    getBackgroundColor,
    getBorderStyle,
    createCardData
} from '../utils/stackUtils';
import { DNDEditor, QwantaProvider } from 'qwanyx';

export const StackItem = ({
    icon,
    text: initialText,
    isFolder = false,
    level = 0,
    selected = false,
    selectedItems = [],
    onSelect,
    itemId,
    isRoot = false,
    registerItem = null,
    itemData = null,
    onRename = null,
    handleMoveItem = null,
    handleUpdateTitle = null,
    handleMakeFolder = null,
    refreshTrigger = 0,
    toggleExpanded = null,
    isExpanded = null,
    toggleFolderFilter = null,
    updateFolderFilterText = null,
    getFolderFilter = null,
    isRecentlyCreated = null,  // NEW: Function to check if item is recently created
    rows = 50,
    disableCurrentCardUpdate = false,
    getProcessedTemplateForItem = null,
    useCompactRenderer = true,
    useFullTemplateMode = true
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [children, setChildren] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [localItemData, setLocalItemData] = useState(itemData);
    const [isEditing, setIsEditing] = useState(false);
    const [displayText, setDisplayText] = useState(initialText);
    const [editText, setEditText] = useState(initialText);

    const inputRef = useRef(null);
    const iconRef = useRef(null);

    // Get Qwanyx context for card update functionality
    const { updateCardByName, showCard } = useQwanyx();

    // Use drag and drop hook with local-first handlers
    const {
        isHovering,
        isHoveringOverIcon,
        handleDragStart,
        handleDragEnd,
        handleDragEnter,
        handleDragOver,
        handleDragLeave,
        handleDrop
    } = useStackDragDrop(handleMoveItem, handleMakeFolder, false);

    // Memoize fetchChildren to safely use in useEffect dependencies
    const fetchChildren = useCallback(async () => {
        if (!itemId) return;

        setIsLoading(true);
        try {
            const childItems = await getChildrenItems(itemId);

            childItems.forEach(item => {
                if (item._id === itemId && !localItemData) {
                    setLocalItemData(item);
                }
            });

            const childrenItems = transformToTreeData(childItems);
            setChildren(childrenItems);

            // Debug logging for root fetches
            if (isRoot) {
                console.log(`[StackItem] Root fetching children for itemId: ${itemId}`);
                console.log(`[StackItem] Root fetched ${childItems.length} children:`, childItems.map(c => c.title || c._id));
            }
        } catch (error) {
            console.error("Error fetching children:", error);
        } finally {
            setIsLoading(false);
        }
    }, [itemId, localItemData, isRoot]);

    useEffect(() => {
        setDisplayText(initialText);
    }, [initialText]);

    useEffect(() => {
        if (registerItem && itemId && !isRoot) {
            registerItem(itemId, level, localItemData);
        }
    }, [registerItem, itemId, level, isRoot, localItemData]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    // NEW: Sync local isOpen state with global expanded state
    useEffect(() => {
        if (!isRoot && itemId && isExpanded) {
            const shouldBeOpen = isExpanded(itemId);
            if (shouldBeOpen !== isOpen) {
                setIsOpen(shouldBeOpen);
            }
        }
    }, [itemId, isExpanded, isOpen, isRoot]);

    // NEW: Initialize expanded state for folders
    useEffect(() => {
        if (isFolder && !isRoot && itemId && toggleExpanded && isExpanded) {
            // If this folder was previously expanded, restore that state
            const wasExpanded = isExpanded(itemId);
            if (wasExpanded && !isOpen) {
                setIsOpen(true);
            }
        }
    }, [isFolder, isRoot, itemId, toggleExpanded, isExpanded, isOpen]);

    // SIMPLIFIED: Only fetch on initial mount or folder open - PLUS respond to cardDataMap changes
    useEffect(() => {
        if (isRoot) {
            fetchChildren();
            return;
        }

        if (isFolder && isOpen && children.length === 0) {
            fetchChildren();
        }
    }, [isRoot, isFolder, isOpen, children.length, fetchChildren]);

    // ENHANCED: Respond to refresh triggers for local-first updates
    useEffect(() => {
        if (refreshTrigger > 0 && isRoot) {
            console.log(`[StackItem] Root responding to refresh trigger: ${refreshTrigger}`);
            fetchChildren();
        }
    }, [refreshTrigger, isRoot, fetchChildren]);

    // NEW: Filter children based on folder-specific filter
    const getFilteredChildren = useCallback(() => {
        if (!getFolderFilter || !itemId) return children;
        
        const folderFilter = getFolderFilter(itemId);
        if (!folderFilter.text) {
            return children;
        }

        return children.filter(child => {
            const searchText = folderFilter.text.toLowerCase();
            const childTitle = (child.itemData?.title || '').toLowerCase();
            const childFirstname = (child.itemData?.firstname || '').toLowerCase();
            const childName = (child.itemData?.name || '').toLowerCase();

            return childTitle.includes(searchText) || 
                   childFirstname.includes(searchText) || 
                   childName.includes(searchText);
        });
    }, [children, getFolderFilter, itemId]);

    // NEW: Sort children alphabetically
    const getSortedChildren = useCallback((childrenToSort) => {
        return [...childrenToSort].sort((a, b) => {
            // Get display name for sorting
            const getDisplayName = (child) => {
                if (child.itemData?.title) {
                    return child.itemData.title.toLowerCase();
                }
                if (child.itemData?.firstname && child.itemData?.name) {
                    return `${child.itemData.firstname} ${child.itemData.name}`.toLowerCase();
                }
                return (child.text || '').toLowerCase();
            };

            const nameA = getDisplayName(a);
            const nameB = getDisplayName(b);
            
            return nameA.localeCompare(nameB);
        });
    }, []);

    // NEW: Get filtered AND sorted children with recently created items at top
    const getProcessedChildren = useCallback(() => {
        const filtered = getFilteredChildren();
        const sorted = getSortedChildren(filtered);
        
        // Separate recently created items from others
        const recentlyCreatedChildren = [];
        const normalChildren = [];
        
        sorted.forEach(child => {
            if (isRecentlyCreated && isRecentlyCreated(child.id)) {
                recentlyCreatedChildren.push(child);
            } else {
                normalChildren.push(child);
            }
        });
        
        // Recently created items go first, then normal sorted items
        return [...recentlyCreatedChildren, ...normalChildren];
    }, [getFilteredChildren, getSortedChildren, isRecentlyCreated]);

    // NEW: Check if this folder should show filter input
    const shouldShowFilterInput = () => {
        return isFolder && 
               !isRoot && 
               isOpen &&
               selectedItems.length === 1 && 
               selectedItems[0] === itemId &&
               updateFolderFilterText;
    };

    // NEW: Get current filter state for this folder
    const currentFilter = getFolderFilter ? getFolderFilter(itemId) : { active: false, text: '' };
    const toggleFolder = (e) => {
        if (isFolder || isRoot) {
            e.stopPropagation();
            
            if (!isRoot && toggleExpanded) {
                // Use global expanded state management
                toggleExpanded(itemId);
            } else {
                // Fallback to local state for root
                setIsOpen(!isOpen);
            }
        }
    };

    const updateCurrentCard = () => {
        if (!itemId) return;

        const cardData = createCardData(localItemData, itemId, displayText);
        updateCardByName("currentCard", cardData);
        showCard("currentCard");
    };

    const handleClick = (e) => {
        if (document.querySelector('.dragging')) return;

        if (!isRoot && onSelect) {
            onSelect(itemId, e.shiftKey, e.ctrlKey || e.metaKey);

            if (!e.shiftKey && !e.ctrlKey && !e.metaKey && !disableCurrentCardUpdate) {
                updateCurrentCard();
            }
        }
    };

    const handleDoubleClick = (e) => {
        e.stopPropagation();
        setIsEditing(true);
        setEditText(displayText);
    };

    const handleInputChange = (e) => {
        setEditText(e.target.value);
    };

    const handleInputBlur = () => {
        finishEditing();
    };

    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            finishEditing();
        } else if (e.key === 'Escape') {
            cancelEditing();
        }
    };

    // ENHANCED: Local-first title editing
    const finishEditing = async () => {
        setIsEditing(false);

        if (editText !== displayText && editText.trim() !== '') {
            setDisplayText(editText);

            if (localItemData) {
                const updatedItemData = {
                    ...localItemData,
                    title: editText
                };
                setLocalItemData(updatedItemData);
            }

            try {
                // Use local-first title update if available
                if (handleUpdateTitle) {
                    await handleUpdateTitle(itemId, editText);
                }

                if (!disableCurrentCardUpdate) {
                    updateCurrentCard();
                }
            } catch (error) {
                console.error("Failed to update title:", error);
                // Revert on error
                setDisplayText(displayText);
                if (localItemData) {
                    setLocalItemData(localItemData);
                }
            }

            if (onRename) {
                onRename(itemId, editText);
            }
        } else {
            setEditText(displayText);
        }
    };

    const cancelEditing = () => {
        setIsEditing(false);
        setEditText(displayText);
    };

    const isSelected = selectedItems.includes(itemId);

    if (isRoot) {
        const processedChildren = getProcessedChildren();
        const hasMoreChildren = processedChildren.length > rows;
        const displayChildren = hasMoreChildren ? processedChildren.slice(0, rows) : processedChildren;

        return (
            <div style={{ width: '100%', overflow: 'auto' }}>
                {isLoading ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>
                ) : displayChildren.length > 0 ? (
                    <>
                        {displayChildren.map((child) => (
                            <StackItem
                                key={child.id}
                                itemId={child.id}
                                icon={child.icon}
                                text={child.text}
                                isFolder={child.isFolder}
                                selectedItems={selectedItems}
                                onSelect={onSelect}
                                registerItem={registerItem}
                                itemData={child.itemData}
                                onRename={onRename}
                                handleMoveItem={handleMoveItem}
                                handleUpdateTitle={handleUpdateTitle}
                                handleMakeFolder={handleMakeFolder}
                                refreshTrigger={refreshTrigger}
                                toggleExpanded={toggleExpanded}
                                isExpanded={isExpanded}
                                toggleFolderFilter={toggleFolderFilter}
                                updateFolderFilterText={updateFolderFilterText}
                                getFolderFilter={getFolderFilter}
                                isRecentlyCreated={isRecentlyCreated}  // NEW: Pass recently created checker
                                rows={rows}
                                disableCurrentCardUpdate={disableCurrentCardUpdate}
                                getProcessedTemplateForItem={getProcessedTemplateForItem}
                                useCompactRenderer={useCompactRenderer}
                                useFullTemplateMode={useFullTemplateMode}
                            />
                        ))}
                        {hasMoreChildren && (
                            <div style={{ padding: '10px', textAlign: 'center', color: '#666' }}>
                                Showing {rows} of {processedChildren.length} items (sorted A-Z)
                            </div>
                        )}
                    </>
                ) : children.length > 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                        No items match current filter
                    </div>
                ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                        No items available
                    </div>
                )}
            </div>
        );
    }

    const folderIcon = isFolder
        ? isOpen
            ? 'fa-folder-open'
            : 'fa-folder'
        : icon;

    // Check if we should use full template mode
    const shouldUseFullTemplate = !isRoot && getProcessedTemplateForItem && useFullTemplateMode;
    const templateResult = shouldUseFullTemplate ? getProcessedTemplateForItem(itemId) : null;

    // Full template mode - template takes complete control
    if (templateResult && shouldUseFullTemplate) {
        console.log(`[StackItem ${itemId}] ✅ Full template mode - complete takeover`);

        return (
            <>
                <div
                    style={{
                        width: '100%',
                        cursor: 'pointer',
                        backgroundColor: getBackgroundColor(isSelected, isHovering),
                        transition: 'all 0.2s ease',
                        position: 'relative'
                    }}
                    onClick={handleClick}
                    draggable={!isRoot}
                    onDragStart={(e) => handleDragStart(e, itemId, isFolder, displayText, localItemData, isRoot)}
                    onDragEnd={handleDragEnd}
                    onDragEnter={(e) => handleDragEnter(e, iconRef, isRoot)}
                    onDragOver={(e) => handleDragOver(e, iconRef, isRoot)}
                    onDragLeave={(e) => handleDragLeave(e, iconRef)}
                    onDrop={(e) => handleDrop(e, itemId, isFolder, iconRef, localItemData)}
                    className="stack-item"
                >
                    {isHovering && (
                        <div
                            style={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: '4px',
                                backgroundColor: isFolder ? '#1890ff' : '#40a9ff'
                            }}
                        />
                    )}
                    {isEditing ? (
                        <div style={{ width: '100%', padding: '8px' }}>
                            <input
                                ref={inputRef}
                                type="text"
                                value={editText}
                                className="input is-small"
                                onChange={handleInputChange}
                                onBlur={handleInputBlur}
                                onKeyDown={handleInputKeyDown}
                                style={{ width: '100%' }}
                                onClick={(e) => e.stopPropagation()}
                                onDoubleClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    ) : (
                        <QwantaProvider _id={itemId}>
                            <DNDEditor
                                cardData={templateResult}
                                enabled={false}
                                readOnly={true}
                                stackMode={true}
                                compact={useCompactRenderer}
                            />
                        </QwantaProvider>
                    )}
                </div>

                {/* Filter input for full template mode */}
                {shouldShowFilterInput() && (
                    <div style={{
                        paddingLeft: `${10 + level * 20}px`,
                        paddingRight: '10px',
                        paddingTop: '5px',
                        paddingBottom: '5px',
                        backgroundColor: '#f8f9fa',
                        borderBottom: '1px solid #eee'
                    }}>
                        <input
                            type="text"
                            placeholder="Filter by title, firstname, name..."
                            value={currentFilter.text}
                            onChange={(e) => updateFolderFilterText && updateFolderFilterText(itemId, e.target.value)}
                            style={{
                                width: '100%',
                                padding: '4px 8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '12px'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                )}

                {/* Children rendering for full template folders */}
                {isOpen && isFolder && (() => {
                    const processedChildren = getProcessedChildren();
                    const hasMoreFolderChildren = processedChildren.length > rows;

                    if (processedChildren.length > 0 && hasMoreFolderChildren) {
                        return (
                            <div style={{ maxHeight: `${rows * 50}px`, overflow: 'auto' }}>
                                {processedChildren.slice(0, rows).map((child) => (
                                    <StackItem
                                        key={child.id}
                                        icon={child.icon}
                                        text={child.text}
                                        isFolder={child.isFolder}
                                        level={level + 1}
                                        itemId={child.id}
                                        selectedItems={selectedItems}
                                        onSelect={onSelect}
                                        registerItem={registerItem}
                                        itemData={child.itemData}
                                        onRename={onRename}
                                        handleMoveItem={handleMoveItem}
                                        handleUpdateTitle={handleUpdateTitle}
                                        handleMakeFolder={handleMakeFolder}
                                        refreshTrigger={refreshTrigger}
                                        toggleExpanded={toggleExpanded}
                                        isExpanded={isExpanded}
                                        toggleFolderFilter={toggleFolderFilter}
                                        updateFolderFilterText={updateFolderFilterText}
                                        getFolderFilter={getFolderFilter}
                                        isRecentlyCreated={isRecentlyCreated}  // NEW: Pass recently created checker
                                        rows={rows}
                                        disableCurrentCardUpdate={disableCurrentCardUpdate}
                                        getProcessedTemplateForItem={getProcessedTemplateForItem}
                                        useCompactRenderer={useCompactRenderer}
                                        useFullTemplateMode={useFullTemplateMode}
                                    />
                                ))}
                            </div>
                        );
                    } else if (processedChildren.length > 0) {
                        return processedChildren.map((child) => (
                            <StackItem
                                key={child.id}
                                icon={child.icon}
                                text={child.text}
                                isFolder={child.isFolder}
                                level={level + 1}
                                itemId={child.id}
                                selectedItems={selectedItems}
                                onSelect={onSelect}
                                registerItem={registerItem}
                                itemData={child.itemData}
                                onRename={onRename}
                                handleMoveItem={handleMoveItem}
                                handleUpdateTitle={handleUpdateTitle}
                                handleMakeFolder={handleMakeFolder}
                                refreshTrigger={refreshTrigger}
                                toggleExpanded={toggleExpanded}
                                isExpanded={isExpanded}
                                toggleFolderFilter={toggleFolderFilter}
                                updateFolderFilterText={updateFolderFilterText}
                                getFolderFilter={getFolderFilter}
                                isRecentlyCreated={isRecentlyCreated}  // NEW: Pass recently created checker
                                rows={rows}
                                disableCurrentCardUpdate={disableCurrentCardUpdate}
                                getProcessedTemplateForItem={getProcessedTemplateForItem}
                                useCompactRenderer={useCompactRenderer}
                                useFullTemplateMode={useFullTemplateMode}
                            />
                        ));
                    } else if (children.length === 0 && !isLoading) {
                        return (
                            <div style={{
                                padding: '10px',
                                paddingLeft: `${10 + (level + 1) * 20}px`,
                                color: '#999',
                                fontStyle: 'italic'
                            }}>
                                No items
                            </div>
                        );
                    } else if (currentFilter.text) {
                        return (
                            <div style={{
                                padding: '10px',
                                paddingLeft: `${10 + (level + 1) * 20}px`,
                                color: '#999',
                                fontStyle: 'italic'
                            }}>
                                No items match "{currentFilter.text}"
                            </div>
                        );
                    }
                    return null;
                })()}
            </>
        );
    }

    // Standard rendering (existing code with normal formatting)
    return (
        <>
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px',
                    paddingLeft: `${10 + level * 20}px`,
                    borderBottom: getBorderStyle(isHovering, isFolder),
                    cursor: 'pointer',
                    backgroundColor: getBackgroundColor(isSelected, isHovering),
                    transition: 'all 0.2s ease',
                    position: 'relative'
                }}
                onClick={handleClick}
                draggable={!isRoot}
                onDragStart={(e) => handleDragStart(e, itemId, isFolder, displayText, localItemData, isRoot)}
                onDragEnd={handleDragEnd}
                onDragEnter={(e) => handleDragEnter(e, iconRef, isRoot)}
                onDragOver={(e) => handleDragOver(e, iconRef, isRoot)}
                onDragLeave={(e) => handleDragLeave(e, iconRef)}
                onDrop={(e) => handleDrop(e, itemId, isFolder, iconRef, localItemData)}
                className="stack-item"
            >
                {isHovering && (
                    <div
                        style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: '4px',
                            backgroundColor: isFolder ? '#1890ff' : '#40a9ff'
                        }}
                    />
                )}
                <span style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    {isFolder ? (
                        <i
                            ref={iconRef}
                            className={`fas ${folderIcon} small`}
                            onClick={toggleFolder}
                            style={{
                                cursor: 'pointer',
                                color: isHoveringOverIcon ? '#2ecc71' : 'inherit',
                                transition: 'color 0.2s ease'
                            }}
                        />
                    ) : (
                        <i
                            ref={iconRef}
                            className={`fas ${icon} small`}
                            style={{
                                color: isHoveringOverIcon ? '#2ecc71' : 'inherit',
                                transition: 'color 0.2s ease'
                            }}
                        />
                    )}

                    {isEditing ? (
                        <div style={{ marginLeft: '8px', width: '100%' }}>
                            <input
                                ref={inputRef}
                                type="text"
                                value={editText}
                                className="input is-small"
                                onChange={handleInputChange}
                                onBlur={handleInputBlur}
                                onKeyDown={handleInputKeyDown}
                                style={{ width: '100%' }}
                                onClick={(e) => e.stopPropagation()}
                                onDoubleClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    ) : (() => {
                        // Template rendering with normal formatting (icon + template)
                        if (!isRoot && getProcessedTemplateForItem && !useFullTemplateMode) {
                            const templateResult = getProcessedTemplateForItem(itemId);

                            if (templateResult) {
                                console.log(`[StackItem ${itemId}] ✅ Rendering DNDEditor with icon and compact=${useCompactRenderer}`);

                                return (
                                    <div style={{
                                        marginLeft: '8px',
                                        width: '100%',
                                        minHeight: useCompactRenderer ? '20px' : 'auto',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between'
                                    }}>
                                        <div style={{ flex: 1 }}>
                                            <QwantaProvider _id={itemId}>
                                                <DNDEditor
                                                    cardData={templateResult}
                                                    enabled={false}
                                                    readOnly={true}
                                                    stackMode={true}
                                                    compact={useCompactRenderer}
                                                />
                                            </QwantaProvider>
                                        </div>
                                    </div>
                                );
                            }
                        }

                        // Default text display with double-click editing
                        return (
                            <span
                                style={{
                                    flex: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: 'block',
                                    lineHeight: '1.2'
                                }}
                                onDoubleClick={handleDoubleClick}
                            >
                                {displayText}
                            </span>
                        );
                    })()}
                </span>
                {isFolder && isLoading && (
                    <span style={{ fontSize: '12px', color: '#999' }}>Loading...</span>
                )}
            </div>

            {/* Filter input - shows when folder is open AND selected */}
            {shouldShowFilterInput() && (
                <div style={{
                    paddingLeft: `${10 + level * 20}px`,
                    paddingRight: '10px',
                    paddingTop: '5px',
                    paddingBottom: '5px',
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #eee'
                }}>
                    <input
                        type="text"
                        placeholder="Filter by title, firstname, name..."
                        value={currentFilter.text}
                        onChange={(e) => updateFolderFilterText && updateFolderFilterText(itemId, e.target.value)}
                        style={{
                            width: '100%',
                            padding: '4px 8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '12px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}

            {/* Children rendering for folders */}
            {isOpen && isFolder && (() => {
                const processedChildren = getProcessedChildren();
                const hasMoreFolderChildren = processedChildren.length > rows;

                if (processedChildren.length > 0 && hasMoreFolderChildren) {
                    return (
                        <div style={{ maxHeight: `${rows * 50}px`, overflow: 'auto' }}>
                            {processedChildren.slice(0, rows).map((child) => (
                                <StackItem
                                    key={child.id}
                                    icon={child.icon}
                                    text={child.text}
                                    isFolder={child.isFolder}
                                    level={level + 1}
                                    itemId={child.id}
                                    selectedItems={selectedItems}
                                    onSelect={onSelect}
                                    registerItem={registerItem}
                                    itemData={child.itemData}
                                    onRename={onRename}
                                    handleMoveItem={handleMoveItem}
                                    handleUpdateTitle={handleUpdateTitle}
                                    handleMakeFolder={handleMakeFolder}
                                    refreshTrigger={refreshTrigger}
                                    toggleExpanded={toggleExpanded}
                                    isExpanded={isExpanded}
                                    toggleFolderFilter={toggleFolderFilter}
                                    updateFolderFilterText={updateFolderFilterText}
                                    getFolderFilter={getFolderFilter}
                                    rows={rows}
                                    disableCurrentCardUpdate={disableCurrentCardUpdate}
                                    getProcessedTemplateForItem={getProcessedTemplateForItem}
                                    useCompactRenderer={useCompactRenderer}
                                    useFullTemplateMode={useFullTemplateMode}
                                />
                            ))}
                        </div>
                    );
                } else if (processedChildren.length > 0) {
                    return processedChildren.map((child) => (
                        <StackItem
                            key={child.id}
                            icon={child.icon}
                            text={child.text}
                            isFolder={child.isFolder}
                            level={level + 1}
                            itemId={child.id}
                            selectedItems={selectedItems}
                            onSelect={onSelect}
                            registerItem={registerItem}
                            itemData={child.itemData}
                            onRename={onRename}
                            handleMoveItem={handleMoveItem}
                            handleUpdateTitle={handleUpdateTitle}
                            handleMakeFolder={handleMakeFolder}
                            refreshTrigger={refreshTrigger}
                            toggleExpanded={toggleExpanded}
                            isExpanded={isExpanded}
                            toggleFolderFilter={toggleFolderFilter}
                            updateFolderFilterText={updateFolderFilterText}
                            getFolderFilter={getFolderFilter}
                            isRecentlyCreated={isRecentlyCreated}  // NEW: Pass recently created checker
                            rows={rows}
                            disableCurrentCardUpdate={disableCurrentCardUpdate}
                            getProcessedTemplateForItem={getProcessedTemplateForItem}
                            useCompactRenderer={useCompactRenderer}
                            useFullTemplateMode={useFullTemplateMode}
                        />
                    ));
                } else if (children.length === 0 && !isLoading) {
                    return (
                        <div style={{
                            padding: '10px',
                            paddingLeft: `${10 + (level + 1) * 20}px`,
                            color: '#999',
                            fontStyle: 'italic'
                        }}>
                            No items
                        </div>
                    );
                } else if (currentFilter.text) {
                    return (
                        <div style={{
                            padding: '10px',
                            paddingLeft: `${10 + (level + 1) * 20}px`,
                            color: '#999',
                            fontStyle: 'italic'
                        }}>
                            No items match "{currentFilter.text}"
                        </div>
                    );
                }
                return null;
            })()}
        </>
    );
};

export default StackItem;